package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.ImageDetails
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private val dialog = Bem("admin-dialog")
private val field = Bem("admin-field")
private val focalPoint = Bem("admin-focal-point")
private val stack = Bem("admin-stack")

private val DialogElement = ElementBuilder { document.createElement("dialog") as HTMLElement }
private val FigureElement = ElementBuilder { document.createElement("figure") as HTMLElement }
private val FigcaptionElement = ElementBuilder { document.createElement("figcaption") as HTMLElement }

/** The crops the public site uses (#99), as CSS aspect ratios. */
private val CROPS = listOf("4:5" to "4 / 5", "4:3" to "4 / 3", "3:2" to "3 / 2", "1:1" to "1 / 1")

/** How far an arrow key moves the focal point, in percent; with Shift [FINE_STEP]. */
private const val STEP = 5
private const val FINE_STEP = 1

/** "40 % von links, 30 % von oben" */
internal fun focalPointText(x: Int, y: Int): String = "$x % von links, $y % von oben"

/** The focal point after [key] (with [shift]) at [x], [y], or `null` for a key that doesn't move it. */
internal fun focalPointAfterKey(key: String, shift: Boolean, x: Int, y: Int): Pair<Int, Int>? {
    val step = if (shift) FINE_STEP else STEP
    return when (key) {
        "ArrowLeft" -> (x - step).coerceIn(0, 100) to y
        "ArrowRight" -> (x + step).coerceIn(0, 100) to y
        "ArrowUp" -> x to (y - step).coerceIn(0, 100)
        "ArrowDown" -> x to (y + step).coerceIn(0, 100)
        "Home" -> ImageDetails.CENTRE to ImageDetails.CENTRE
        else -> null
    }
}

/**
 * The image details (#105) as a wide modal `<dialog>`: the focal point on the image ("Wichtigster Bildteil", a 2D
 * slider: click, arrows 5 %, Shift+arrows 1 %, Home re-centres) with the public site's crops, the description and the
 * credit. "Übernehmen" hands the details to [onApply], which changes the record's draft; Escape and "Abbrechen" cancel.
 * Opening it starts from [details].
 */
@Composable
fun AdminImageDetailsDialog(
    open: Boolean,
    id: String,
    imageUrl: String?,
    details: ImageDetails,
    onApply: (ImageDetails) -> Unit,
    onCancel: () -> Unit,
) {
    var element by remember { mutableStateOf<HTMLElement?>(null) }
    val currentOnCancel by rememberUpdatedState(onCancel)
    var draft by remember { mutableStateOf(details) }

    LaunchedEffect(open, details) {
        if (open) {
            draft = details
        }
    }

    DisposableEffect(open, element) {
        val dialogElement = element?.asDynamic()
        if (dialogElement != null) {
            if (open && dialogElement.open != true) {
                dialogElement.showModal()
            } else if (!open && dialogElement.open == true) {
                dialogElement.close()
            }
        }
        onDispose { }
    }

    TagElement(DialogElement, {
        classes(dialog.block, dialog.modifier("wide"))
        id(id)
        attr("aria-labelledby", "$id-title")
        ref {
            element = it
            onDispose { element = null }
        }
        // Escape
        addEventListener("cancel") { event ->
            event.preventDefault()
            currentOnCancel()
        }
    }) {
        if (!open || imageUrl == null) {
            return@TagElement
        }

        H2({
            classes(dialog.element("title").className)
            id("$id-title")
        }) { Text("Bilddetails") }

        // the stage, the fields and the actions, spaced like an editor's cards
        Div({ classes(stack.block) }) {
            Div({
                classes(field.block)
                attr("role", "group")
                attr("aria-labelledby", "$id-focal-label")
            }) {
                Span({
                    classes(field.element("label").className)
                    id("$id-focal-label")
                }) {
                    Text("Wichtigster Bildteil")
                    BetaBadge()
                }
                FocalPoint(id, imageUrl, draft.focalX, draft.focalY, current = { draft.focalX to draft.focalY }) { x, y ->
                    draft = draft.copy(focalX = x, focalY = y)
                }
                Span({
                    classes(field.element("hint").className)
                    id("$id-focal-hint")
                }) { Text("Klicke auf das, was beim Zuschneiden sichtbar bleiben soll, oder verschiebe die Markierung mit den Pfeiltasten.") }
            }

            val descriptionLength = draft.description.trim().length
            AdminTextField(
                id = "$id-beschreibung",
                label = "Beschreibung",
                value = draft.description,
                onValueChange = { draft = draft.copy(description = it) },
                error = if (descriptionLength > ImageDetails.MAX_DESCRIPTION_LENGTH) {
                    "Bitte höchstens ${ImageDetails.MAX_DESCRIPTION_LENGTH} Zeichen (jetzt $descriptionLength)."
                } else {
                    null
                },
                hint = "Was auf dem Bild zu sehen ist, für Menschen, die es nicht sehen.",
                required = false,
                autocomplete = "off",
                beta = true,
            )
            val creditLength = draft.credit.trim().length
            AdminTextField(
                id = "$id-foto",
                label = "Foto",
                value = draft.credit,
                onValueChange = { draft = draft.copy(credit = it) },
                error = if (creditLength > ImageDetails.MAX_CREDIT_LENGTH) {
                    "Bitte höchstens ${ImageDetails.MAX_CREDIT_LENGTH} Zeichen (jetzt $creditLength)."
                } else {
                    null
                },
                hint = "Steht klein unter dem Bild, z. B. „Foto: Hans Salzinger“.",
                optional = true,
                autocomplete = "off",
                beta = true,
            )

            Div({ classes(dialog.element("actions").className) }) {
                AdminButton(onClick = onCancel) { Text("Abbrechen") }
                AdminButton(
                    variant = ButtonVariant.PRIMARY,
                    disabled = descriptionLength > ImageDetails.MAX_DESCRIPTION_LENGTH || creditLength > ImageDetails.MAX_CREDIT_LENGTH,
                    onClick = { onApply(draft.copy(description = draft.description.trim(), credit = draft.credit.trim())) },
                ) { Text("Übernehmen") }
            }
        }
    }
}

private fun setFocalPointValue(stage: HTMLElement, point: Pair<Int, Int>) {
    stage.setAttribute("aria-valuenow", point.first.toString())
    stage.setAttribute("aria-valuetext", focalPointText(point.first, point.second))
}

/**
 * The image as a 2D slider with the focal point at [x], [y] marked, and the crops that keep it. Keys move the point
 * from [current], which is read when the key is pressed: a second press may come before the first one was composed.
 */
@Composable
private fun FocalPoint(id: String, imageUrl: String, x: Int, y: Int, current: () -> Pair<Int, Int>, onChange: (Int, Int) -> Unit) {
    Div({ classes(focalPoint.block) }) {
        Div({
            classes(focalPoint.element("stage").className)
            attr("role", "slider")
            attr("tabindex", "0")
            attr("aria-labelledby", "$id-focal-label")
            attr("aria-describedby", "$id-focal-hint")
            attr("aria-valuemin", "0")
            attr("aria-valuemax", "100")
            // set apart from the attributes: when one of them changes, Compose HTML removes and re-adds them all,
            // tabindex too, and the stage would lose the focus after every key (#137)
            prop(::setFocalPointValue, x to y)
            onClick { event ->
                val stage = event.currentTarget as HTMLElement
                val box = stage.getBoundingClientRect()
                if (box.width > 0 && box.height > 0) {
                    val newX = ((event.clientX - box.left) / box.width * 100).toInt().coerceIn(0, 100)
                    val newY = ((event.clientY - box.top) / box.height * 100).toInt().coerceIn(0, 100)
                    onChange(newX, newY)
                }
            }
            onKeyDown { event ->
                val (currentX, currentY) = current()
                focalPointAfterKey(event.key, event.shiftKey, currentX, currentY)?.let { (newX, newY) ->
                    event.preventDefault()
                    onChange(newX, newY)
                }
            }
        }) {
            Img(imageUrl, attrs = {
                classes(focalPoint.element("image").className)
                alt("")
                attr("draggable", "false")
            })
            Span({
                classes(focalPoint.element("marker").className)
                style {
                    property("left", "$x%")
                    property("top", "$y%")
                }
            })
        }
        Div({ classes(focalPoint.element("crops").className) }) {
            CROPS.forEach { (label, ratio) ->
                TagElement(FigureElement, { classes(focalPoint.element("crop").className) }) {
                    Img(imageUrl, attrs = {
                        classes(focalPoint.element("crop-image").className)
                        alt("")
                        style {
                            property("aspect-ratio", ratio)
                            property("object-position", "$x% $y%")
                        }
                    })
                    TagElement(FigcaptionElement, null) { Text(label) }
                }
            }
        }
    }
}
